import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  CATEGORY_ALL,
  STARTUP_THRESHOLDS_MS,
  ZAP_SETTLE_MS,
  buildGuideCategories,
  canWatchFromStart,
  channelsOfCategory,
  moveCategoryCursor,
  programProgressPercent,
  retryDelayMs,
  retryPolicyFor,
  startupEstimateSeconds,
  startupPhase,
  windowWidthPx,
  zapIndex,
  ProgramStore,
  type Channel,
  type GuideWindow,
  type GatewayError,
  type PlaybackPipeline,
  type Program,
  type StartupPhase,
} from '@player/domain';
import type { FocusPort, PlayerPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { GuideController, type GuideCursor } from '../guide/GuideController.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { Translator } from '../i18n/i18n.js';
import { Busy } from '../components/Busy.js';
import { PlayerOverlay } from '../components/PlayerOverlay.js';
import { GuideScreen } from './GuideScreen.js';

/**
 * La diretta: il canale che si guarda, e lo zapping.
 *
 * ## Perché esiste, e cosa sostituisce
 *
 * Fino a oggi, dopo un accesso riuscito, l'app apriva il **banco di misura**. Non era un difetto ma
 * una fase non fatta, e l'effetto era indistinguibile da un'app rotta: si entrava e si finiva su
 * una schermata di diagnostica.
 *
 * ## Le quattro cose che lo zapping deve fare (06-RIPRODUZIONE §5)
 *
 * 1. **Aspettare che il dito si fermi.** Chi tiene premuto ▼ passa sopra venti canali: senza
 *    attesa si aprirebbero venti sessioni, e lo zapping diventa un attacco alla propria
 *    infrastruttura. `ZAP_SETTLE_MS` — 400 ms — sta nel dominio, non qui.
 * 2. **Mostrare il nome subito**, mentre il video non c'è ancora: è quello che lo rende
 *    sopportabile.
 * 3. **Non bloccarsi su un canale che non parte.** Si segnala e si continua a zappare.
 * 4. **Chiudere il canale precedente**, altrimenti scorrere l'elenco consuma il limite di
 *    connessioni della linea.
 *
 * ## L'attesa è raccontata, mai muta
 *
 * `startupPhase` decide *cosa* dire in base ai millisecondi trascorsi; questa schermata lo mostra.
 * La stima («di solito ci vogliono ~8 secondi») la dice il **server**: se non la manda, l'app non
 * inventa un numero — una stima inventata è un ripiego silenzioso travestito da gentilezza.
 *
 * E «Indietro funziona sempre, subito» non è un modo di dire: il tasto è gestito prima di ogni
 * altra cosa, senza aspettare il tempo massimo dell'avvio.
 */

export interface LiveScreenProps {
  readonly focusPort: FocusPort;
  readonly channels: ReadonlyArray<Channel>;
  /** L'indirizzo da riprodurre per un canale. Lo compone l'adattatore, non la schermata. */
  readonly streamUrlFor: (channel: Channel) => string;
  /** L'indirizzo per rivedere un programma passato, dal suo istante d'inizio. */
  readonly catchupUrlFor: (channel: Channel, startMs: number) => string;
  readonly createPlayer: (video: HTMLVideoElement) => PlayerPort;
  /** La stima del server, in millisecondi, quando c'è. Mai inventata qui. */
  readonly startupEstimateMs?: number | null;
  /**
   * La guida: i programmi già noti, e la richiesta di quelli che servono.
   *
   * La schermata **non** conosce il gateway, e non deve: chiede «mi servono questi canali» e chi
   * sta sopra decide come prenderli. È la stessa giunzione che regge il resto — se per mostrare la
   * guida questa schermata dovesse sapere come si chiama una rotta, portarla su Tizen vorrebbe dire
   * portarci anche l'adattatore.
   */
  readonly programs: ProgramStore;
  readonly guideWindow: GuideWindow;
  readonly serverNowMs: number;
  /**
   * Chiamata quando il dito si ferma sulla guida, con i canali visibili.
   *
   * Solo allora: chi scorre veloce ne attraversa duecento, e chiederli tutti sarebbe la stessa
   * lezione dello zapping — un elenco che scorre non è una richiesta per riga.
   */
  readonly onNeedPrograms: (channelIds: ReadonlyArray<string>) => void;
  /** Gli identificativi dei preferiti: la stella accanto al numero, nella guida. */
  readonly favoriteIds: ReadonlySet<string>;
  /** I promemoria già impostati: li mostra l'anteprima della guida. */
  readonly reminderIds: ReadonlySet<string>;
  /**
   * I nomi delle categorie, per identificativo.
   *
   * Senza, la colonna della guida mostrava l'identificativo — su una installazione vera un UUID.
   * Vuota finché la chiamata non torna: le voci restano con il nome dichiarato «senza nome», mai con
   * l'identificativo grezzo.
   */
  readonly categoryNames?: ReadonlyMap<string, string>;
  /**
   * Inverte un preferito.
   *
   * L'OSD del prototipo ha «Aggiungi ai preferiti» fra i tasti, ed è il posto più naturale in cui
   * premerlo — si è già davanti al canale. Chi possiede l'elenco e manda la differenza al gateway sta
   * sopra; qui si chiede solo l'inversione.
   */
  readonly onToggleFavorite: (channelId: string) => void;
  /**
   * Mette o toglie il promemoria su un programma futuro.
   *
   * La chiave è **canale più istante** (`reminderKey`), non l'identificativo dell'EPG: quello che
   * `materialize` restituisce è sintetizzato dalla posizione, e quello vero cambia a ogni import.
   */
  readonly onToggleReminder?: (channelId: string, startMs: number) => void;
  /** Porta alla schermata dei preferiti: è il tasto della categoria vuota. */
  readonly onOpenFavorites?: () => void;
  /**
   * Indietro chiede **il menu**, che non è più figlio di questa schermata.
   *
   * La colonna sta nel guscio, accanto alle schermate, come nel prototipo: qui si chiede solo che le
   * venga dato il fuoco. Uscire di colpo dal lettore riporterebbe all'accesso, cioè a ridigitare le
   * credenziali, e per chi voleva solo cambiare canale è una punizione.
   */
  readonly onRequestMenu: () => void;
  /**
   * Se i tasti sono del menu.
   *
   * Serve a **tacere**: quando il menu ha il fuoco questa schermata non deve consumare niente,
   * altrimenti la stessa freccia muove la voce del menu e cambia canale sotto.
   */
  readonly menuHasFocus: boolean;
  /**
   * Si tocca invece di premere, cioè si è su un telefono.
   *
   * Cambia due cose e nessuna di stile. **La fascia si ritira da sola** dopo qualche secondo: col
   * telecomando la toglie Indietro, col dito non esiste nessun tasto che lo faccia, e restava davanti
   * all'immagine per sempre. E **un tocco sul video la richiama**, che è il gesto con cui si chiede
   * «fammi vedere i comandi» su qualunque lettore.
   */
  readonly touch?: boolean;
  /** A tutto schermo: lo dichiara chi ospita, perché è lui a togliere marchio e schede. */
  readonly fullscreen?: boolean;
  /**
   * Entra o esce dallo schermo intero.
   *
   * `undefined` dove non ha senso — sul televisore è già tutto schermo — e in quel caso il tasto non
   * compare affatto, invece di comparire e non fare niente.
   */
  readonly onToggleFullscreen?: () => void;
  /**
   * Quando si sta davvero guardando, cioè il video va.
   *
   * Lo sa questa schermata e lo deve sapere il guscio, che su quel fatto decide se nascondere la
   * colonna: a schermo pieno il menu ruberebbe 280 px all'immagine, ed è l'unico momento in cui ha
   * senso toglierlo. Su un errore o un'attesa non si sta guardando niente, e lì un menu nascosto
   * lascia senza via d'uscita chi non conosce il tasto Indietro.
   */
  readonly onWatchingChange: (watching: boolean) => void;
  /*
    Non c'è più un `onExit`: uscire dall'account è un'azione del **menu**, che sta nel guscio.

    Era una voce di questo elenco, e mescolava due cose diverse — «torna indietro» e «disconnetti
    questo apparecchio» — sotto lo stesso nome. Nel prototipo il tasto Esci è staccato dalle voci
    proprio perché è l'unica azione irreversibile della colonna.
  */
  /** Un rifiuto che richiede di ripresentarsi all'accesso. */
  readonly onSendBackToLogin: (error: GatewayError) => void;
  /**
   * Il canale da cui ripartire: l'ultimo guardato su questo apparecchio.
   *
   * Arriva **già letto**, non come promessa. Se lo leggesse questa schermata, il primo disegno
   * aprirebbe il canale in cima all'elenco e il secondo salterebbe a quello ricordato: due sessioni
   * per un'accensione, e la prima buttata dopo 400 ms.
   *
   * È un identificativo e non una posizione: le posizioni cambiano a ogni import del catalogo, e
   * ripartire dalla settima riga di un elenco riordinato è aprire un canale a caso.
   */
  readonly initialChannelId?: string | null;
  /**
   * Si arriva qui **per scegliere**, non per guardare.
   *
   * Sono due porte diverse sulla stessa schermata, e fino a oggi si comportavano uguale. Chi
   * accende l'app vuole che riparta il canale di ieri — quello è l'avvio automatico, ed è
   * desiderato. Chi preme «Guida TV» nel menu, o una piastrella di categoria in Home, sta facendo
   * l'esatto contrario: sta dicendo «fammi vedere cosa c'è». Partire a tutto schermo con l'ultimo
   * canale gli copre proprio la cosa che ha chiesto, e lo obbliga a un tasto in più per tornare
   * dove voleva andare.
   *
   * Con `true` la guida si apre subito e **non si sintonizza niente al montaggio**. L'anteprima
   * dentro la guida parte da sé quando il cursore si ferma, che è il comportamento di un decoder
   * vero e non un avvio a tutto schermo.
   */
  readonly openGuide?: boolean;
  /** La categoria su cui aprire la guida: ci arriva la piastrella «Sfoglia per categoria» della Home. */
  readonly initialCategoryId?: string | null;
  /**
   * Il canale è stato **davvero** sintonizzato: chi sta sopra se lo ricordi.
   *
   * Si chiama quando la sintonia parte, non a ogni pressione: chi tiene premuto ▼ passa sopra venti
   * canali e nessuno di quei venti è «l'ultimo guardato».
   */
  readonly onChannelTuned?: (channelId: string) => void;
}

type Playback =
  | { readonly kind: 'idle' }
  | { readonly kind: 'starting'; readonly sinceMs: number; readonly attempt: number }
  | { readonly kind: 'playing' }
  | {
      readonly kind: 'failed';
      readonly error: GatewayError;
      /**
       * Quale tentativo è fallito.
       *
       * Senza questo numero la ripetizione non ha memoria: riprova sempre «il primo», e siccome il
       * primo ha sempre diritto a un tentativo, riprova **per sempre**. È il difetto che il
       * 28-09-2026 ha tenuto l'app in ciclo mostrando l'errore e l'attesa a turno.
       */
      readonly attempt: number;
    };

export function LiveScreen({
  focusPort,
  channels,
  streamUrlFor,
  catchupUrlFor,
  createPlayer,
  startupEstimateMs,
  programs,
  guideWindow,
  serverNowMs,
  onNeedPrograms,
  favoriteIds,
  reminderIds,
  categoryNames = EMPTY_CATEGORY_NAMES,
  onToggleFavorite,
  onToggleReminder,
  onOpenFavorites,
  onRequestMenu,
  menuHasFocus,
  touch = false,
  fullscreen = false,
  onToggleFullscreen,
  onWatchingChange,
  onSendBackToLogin,
  initialChannelId = null,
  openGuide = false,
  initialCategoryId = null,
  onChannelTuned,
}: LiveScreenProps): ReactNode {
  const translator = useTranslator();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<PlayerPort | null>(null);

  /*
    Si riparte dall'ultimo canale guardato, quando c'è ancora.

    L'inizializzatore è pigro e gira **una volta**: cercare l'identificativo a ogni disegno sarebbe
    una scansione del catalogo per un valore che non cambia. Se quel canale non c'è più — tolto dal
    bouquet, sparito dall'import — si riparte dal primo, che è la cosa giusta da fare e non un
    ripiego silenzioso: l'alternativa sarebbe una schermata vuota per un canale che non esiste.
  */
  const [index, setIndex] = useState(() => {
    if (initialChannelId === null) return 0;
    const at = channels.findIndex((item) => item.id === initialChannelId);
    return at >= 0 ? at : 0;
  });
  // Vedi `openGuide`: è uno **stato iniziale**, non un comando. Da qui in poi la guida è di chi la
  // sta usando, e chiuderla non deve riaprirsi da sola al primo ridisegno.
  const [guideOpen, setGuideOpen] = useState(openGuide);

  /*
    Lo stato della guida: quale categoria, dove sta il cursore nella colonna, e quale area ha i tasti.

    Sta **qui** e non dentro `GuideScreen` perché qui c'è l'unica iscrizione al telecomando. Due
    iscrizioni attive insieme ricevono la stessa freccia: il cursore della griglia si muoverebbe *e*
    si muoverebbe la voce della categoria. E sta qui anche perché sopravvive alla chiusura — chi
    restringe a «Sport», guarda, chiude e riapre non deve rifare la scelta.
  */
  /*
    La categoria su cui si apre la guida.

    È uno **stato iniziale**, non un comando: la Home ci manda chi preme una piastrella di categoria,
    e da lì in poi la guida è di chi la sta usando. Cambiarlo mentre la schermata è aperta sposterebbe
    il filtro sotto le mani.

    Prima la piastrella portava alla guida **senza filtrare**: si premeva «Sport» e si finiva su
    «Tutti», senza capire perché. Una card che porta nel posto sbagliato è peggio di una che non porta.
  */
  const [categoryId, setCategoryId] = useState<string>(initialCategoryId ?? CATEGORY_ALL);
  const [categoryCursor, setCategoryCursor] = useState(0);
  /*
    Dove sono i tasti dentro la guida: la colonna delle categorie, la **card del canale**, o le celle.

    Tre e non due. Nel disegno la card del canale è un `<button>` a sé sulla stessa riga delle celle,
    e OK lì aggiunge o toglie dai preferiti. Senza di lei la stella era premibile solo col mouse: su
    un televisore un elemento che si vede e che il telecomando non raggiunge è il difetto peggiore
    che esista, perché non c'è un puntatore con cui aggirarlo.
  */
  const [guideFocus, setGuideFocus] = useState<'categories' | 'channel' | 'grid'>('grid');

  const [playback, setPlayback] = useState<Playback>({ kind: 'idle' });
  /** Chi sta decodificando, detto dal lettore. Si scrive solo nella riga di diagnosi di un errore. */
  const [pipeline, setPipeline] = useState<PlaybackPipeline>('none');
  /*
    L'audio è in muto perché la piattaforma ha rifiutato l'avvio automatico con il suono.

    Si azzera a ogni cambio di canale: la restrizione riguarda **il gesto**, non il canale, e una volta
    che l'utente l'ha compiuto i canali successivi partono con l'audio. Tenerla accesa farebbe
    comparire per sempre un avviso su un problema che non c'è più.
  */
  const [mutedByPolicy, setMutedByPolicy] = useState(false);
  /*
    **Il muto non è una funzione di questa app.**

    Ci avevo messo un tasto «Attiva/Disattiva audio» nella fascia, ed era sbagliato: il telecomando
    del televisore ha il suo, e ogni apparecchio su cui gira questa app ne ha uno. Avevo trasformato
    in funzione di prodotto un rimedio a un problema del browser — e le funzioni nate così si
    riconoscono dal fatto che rispondono a una domanda che nessuno ha fatto.

    Del rimedio resta solo la parte necessaria, e **senza interfaccia**: `mutedByPolicy` dice che la
    piattaforma ha rifiutato l'audio all'avvio automatico, si scrive perché c'è silenzio, e alla
    prima pressione di un tasto — che è il gesto che il browser aspettava — si riaccende da sé.
  */

  /*
    Se la fascia in basso si vede.

    Compare quando il canale parte e **Indietro la toglie**, lasciando solo il video: è quello che è
    stato chiesto, e ha una ragione — la fascia porta i tasti dei preferiti, che servono per un
    istante e poi stanno davanti all'immagine per sempre.

    Indietro fa quindi due cose in fila, e l'ordine conta: prima toglie la fascia, e solo a fascia già
    tolta chiede il menu. Una sola pressione che facesse entrambe le cose renderebbe impossibile
    ottenere lo schermo pulito, che è la richiesta.
  */
  const [osdVisible, setOsdVisible] = useState(true);

  /*
    Quale azione della fascia ha il fuoco, o `null` quando la fascia non se li prende.

    A schermo intero il primo OK **apre la fascia** e le dà il fuoco: da lì sinistra e destra
    scorrono i tasti e OK preme quello scelto. Era una scelta implicita che il primo OK aprisse la
    guida, e saltava il passo che serve più spesso — mettere il canale fra i preferiti mentre lo si
    sta guardando. Alla guida ci si arriva da un tasto della fascia, che è un posto dove si va a
    cercarla; il contrario non è vero.
  */
  const [osdCursor, setOsdCursor] = useState<number | null>(null);

  /*
    Il canale che si stava guardando **prima** di aprire la guida.

    Entrare nella guida per sbaglio, o cambiare idea, deve costare una pressione di Indietro — non la
    perdita del canale. Senza questo numero l'unico modo di tornare indietro sarebbe ricordarselo e
    ricercarlo nell'elenco, cosa che su dodicimila canali non si fa.

    È un riferimento e non uno stato: cambiarlo non deve ridisegnare niente, e serve solo nel momento
    in cui si preme Indietro.
  */
  const indexBeforeGuide = useRef<number | null>(null);

  /*
    Il programma che si può guardare dall'inizio, quando ce n'è uno.

    Lo riempie la scelta dalla guida e lo svuota lo zapping: è legato **al gesto**, non al canale.
    Chi apre un canale premendo sulla partita di mezz'ora fa ottiene la proposta; chi ci arriva
    zappando no, perché non ha indicato nessun momento.
  */
  const [pendingReplay, setPendingReplay] = useState<{ startMs: number; title: string } | null>(null);
  /** Ridisegna una volta al secondo **solo** mentre si aspetta: il testo deve cambiare. */
  const [, forceTick] = useState(0);

  /*
    «Si sta guardando» è un fatto di questa schermata, e il guscio ci decide se nascondere la colonna.

    Va riferito in un effetto e non durante il disegno: chiamare la funzione del genitore dentro il
    corpo del componente vorrebbe dire cambiare lo stato di chi ci contiene mentre lui ci sta
    disegnando, che React rifiuta — e quando non lo rifiuta produce un ciclo.
  */
  const watching = playback.kind === 'playing' && !guideOpen;
  const onWatchingChangeRef = useRef(onWatchingChange);
  useEffect(() => {
    onWatchingChangeRef.current = onWatchingChange;
  }, [onWatchingChange]);
  useEffect(() => {
    onWatchingChangeRef.current(watching);
  }, [watching]);

  const channel = channels[index];

  /*
    Il canale **richiesto** è separato da quello mostrato: l'indice cambia a ogni pressione, la
    riproduzione parte solo quando il dito si è fermato. Tenerli nello stesso stato significherebbe
    o una sessione per pressione, o un nome che compare in ritardo — e sono le due cose che §5
    vieta.
  */
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPlayer = useCallback(() => {
    // Distruggere e non solo fermare: una sessione lasciata aperta consuma il limite di
    // connessioni della linea, e chi zappa venti canali le aprirebbe tutte e venti.
    playerRef.current?.destroy();
    playerRef.current = null;
  }, []);

  const start = useCallback(
    (target: Channel, attempt: number, url?: string) => {
      const video = videoRef.current;
      if (!video) return;
      stopPlayer();

      const player = createPlayer(video);
      playerRef.current = player;
      setPlayback({ kind: 'starting', sinceMs: Date.now(), attempt });

      player.subscribe((event) => {
        if (event.type === 'firstFrame' || event.type === 'playing') {
          setPlayback({ kind: 'playing' });
          return;
        }
        /*
          Il canale va, ma la piattaforma ha rifiutato l'audio all'avvio automatico.

          Non è un errore: si è **in onda**, e dirlo è ciò che chiude l'attesa. Prima questo caso non
          arrivava affatto — l'adattatore inghiottiva il rifiuto — e l'app restava su «Avvio del
          canale…» fino al tempo massimo per poi dichiarare un guasto su un canale che funzionava.
        */
        if (event.type === 'mutedByPolicy') {
          setMutedByPolicy(true);
          setPlayback({ kind: 'playing' });
          return;
        }
        /*
          Quale strada sta usando il lettore, tenuta da parte per la riga di diagnosi.

          Non si mostra a chi guarda la televisione: compare **solo** accanto a un errore, dove
          serve a chi risponde al telefono. Il 04-10-2026 quella domanda — «ma questo telefono sta
          usando hls.js o il lettore di sistema?» — si poteva solo dedurre dalla versione di iOS, e
          la deduzione era sbagliata.
        */
        if (event.type === 'pipeline') {
          setPipeline(event.pipeline);
          return;
        }
        if (event.type === 'error') {
          setPlayback({ kind: 'failed', error: event.error, attempt });
        }
      });

      player.play(url ?? streamUrlFor(target));
    },
    [createPlayer, stopPlayer, streamUrlFor],
  );

  /** Lo zapping: l'indice si muove subito, la sessione parte quando il dito si ferma. */
  const zap = useCallback(
    (delta: number) => {
      if (channels.length === 0) return;
      setIndex((current) => zapIndex(current, delta, channels.length));
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      // Fermare **subito** il canale di prima: il video che continua mentre il nome è già cambiato
      // è la cosa che fa sembrare l'app in ritardo su sé stessa.
      stopPlayer();
      setPlayback({ kind: 'idle' });
      setMutedByPolicy(false);
      // Zappando la proposta decade: si riferiva a un programma di un altro canale.
      setPendingReplay(null);
      // Cambiando canale la fascia torna: dice **dove si è arrivati**, ed è il momento in cui
      // quell'informazione serve. Restare nascosta farebbe sembrare lo zapping muto.
      setOsdVisible(true);
      // Ricompare per **dire** dove si è arrivati, non per farsi premere: prendersi i tasti a ogni
      // zapping farebbe sparire le frecce del cambio canale proprio mentre si sta zappando.
      setOsdCursor(null);
    },
    [channels.length, stopPlayer],
  );

  /*
    **Col dito la fascia si ritira da sola.**

    Col telecomando la toglie Indietro, ed è giusto così: è un tasto che c'è sempre e il gesto è
    esplicito. Su un telefono quel tasto non esiste — e la fascia restava davanti all'immagine per
    tutto il tempo, proprio in orizzontale, dove si gira il telefono apposta per vedere meglio.

    Tre secondi: il tempo di leggere che canale è e che programma sta andando. Si ritira **solo
    mentre il video va**: durante un'attesa o dopo un errore, quel che c'è scritto lì è l'unica cosa
    che qualcuno sta aspettando di leggere.

    E non si ritira mentre ha il fuoco (`osdCursor`): sarebbe un tasto che sparisce sotto il dito.
  */
  useEffect(() => {
    if (!touch || !osdVisible || osdCursor !== null) return undefined;
    if (playback.kind !== 'playing') return undefined;
    const timer = setTimeout(() => setOsdVisible(false), OSD_AUTOHIDE_MS);
    return () => clearTimeout(timer);
  }, [touch, osdVisible, osdCursor, playback.kind]);

  /*
    Quando l'indice si ferma per `ZAP_SETTLE_MS`, si chiede il canale.

    ## Perché l'effetto dipende dall'**identificativo** e non da `start`

    Perché `start` cambia identità quando cambiano `createPlayer` o `streamUrlFor`, e quelle arrivano
    da fuori: se il chiamante le scrive in linea — come è naturale scriverle — cambiano a **ogni**
    disegno. L'effetto ripartirebbe, richiederebbe il canale, che ridisegna, che cambia di nuovo le
    funzioni: un ciclo che non finisce mai.

    Non è teoria: il 28-09-2026 l'ho scritto così e l'app è rimasta bloccata su «Avvio del canale…»
    per sempre, senza mai passare alla fase successiva e senza mai arrivare al tempo massimo. Da
    fuori sembrava una rotella eterna — esattamente ciò che il §4 esiste per evitare, ottenuto
    scrivendo il codice che lo implementa.

    La cura non è chiedere al chiamante di memorizzare le sue funzioni: quella è una regola che
    qualcuno dimenticherà, e il modo di fallire è questo. La cura è che questa schermata riparta
    **solo** quando cambia il canale, e usi sempre l'ultima versione delle funzioni attraverso un
    riferimento.
  */
  /*
    Le funzioni che arrivano da fuori si leggono da un riferimento, mai dalle dipendenze.

    Un effetto che le elenca si ricrea a ogni disegno, e **la sua pulizia azzera i timer in corso**:
    un'attesa di due secondi non matura mai se qualcuno la cancella ogni cento millisecondi. È la
    stessa causa del ciclo dell'avvio, in un altro punto — e si presenta all'opposto: là ripartiva
    all'infinito, qui non riparte affatto.
  */
  const onChannelTunedRef = useRef(onChannelTuned);
  const needProgramsRef = useRef(onNeedPrograms);
  useEffect(() => {
    onChannelTunedRef.current = onChannelTuned;
    needProgramsRef.current = onNeedPrograms;
  }, [onChannelTuned, onNeedPrograms]);

  const startRef = useRef(start);
  const backToLoginRef = useRef(onSendBackToLogin);
  const requestMenuRef = useRef(onRequestMenu);
  const catchupUrlForRef = useRef(catchupUrlFor);
  const onToggleReminderRef = useRef(onToggleReminder);
  const onToggleFavoriteRef = useRef(onToggleFavorite);
  useEffect(() => {
    startRef.current = start;
    backToLoginRef.current = onSendBackToLogin;
    requestMenuRef.current = onRequestMenu;
    catchupUrlForRef.current = catchupUrlFor;
    onToggleFavoriteRef.current = onToggleFavorite;
    onToggleReminderRef.current = onToggleReminder;
  }, [start, onSendBackToLogin, onRequestMenu, catchupUrlFor, onToggleFavorite]);

  /*
    Quando si arriva per scegliere, il **primo** giro di questo effetto non sintonizza.

    L'effetto qui sotto parte anche al montaggio, non solo ai cambi di canale: è lì che nasceva
    l'avvio automatico anche per chi aveva premuto «Guida TV». Si salta una volta sola e per
    riferimento, non per stato: uno stato farebbe ripartire l'effetto e il canale scelto dalla
    guida — che è il giro **successivo** — non partirebbe.
  */
  const skipFirstTune = useRef(openGuide);

  const channelId = channel?.id;
  useEffect(() => {
    if (!channel) return undefined;
    if (skipFirstTune.current) {
      skipFirstTune.current = false;
      return undefined;
    }
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      startRef.current(channel, 0);
      // Solo adesso il canale è «quello guardato»: chi tiene premuto ▼ ne attraversa venti, e
      // nessuno dei venti va ricordato.
      onChannelTunedRef.current?.(channel.id);
      /*
        **E si chiede la sua guida.**

        I programmi si chiedevano solo quando il dito si fermava *dentro la guida*: un canale aperto
        all'accensione, o raggiunto zappando, non ne aveva mai. La fascia scriveva «Programma non
        disponibile» su un canale che l'EPG ce l'aveva — e sembrava un difetto della mappatura, non
        una richiesta mai partita.

        Si chiede **solo qui**, cioè quando il dito si è fermato: chiederla a ogni pressione sarebbe
        la stessa lezione dello zapping, una richiesta per canale attraversato.
      */
      needProgramsRef.current([channel.id]);
    }, ZAP_SETTLE_MS);
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
    // `channel` si rilegge dentro, ma l'effetto riparte solo se cambia **quale** canale è.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  /*
    Il battito che fa cambiare il testo.

    Gira **solo** mentre si aspetta, e non mentre si guarda: un intervallo sempre acceso su una Fire
    TV Stick è un costo che si paga per niente. Un secondo è la granularità giusta — le soglie del
    §4 sono a 3, 8 e 15 secondi.
  */
  useEffect(() => {
    if (playback.kind !== 'starting') return undefined;
    const timer = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [playback.kind]);

  // Il tempo massimo: oltre l'ultima soglia non si aspetta più, si dichiara.
  useEffect(() => {
    if (playback.kind !== 'starting') return undefined;
    const elapsed = Date.now() - playback.sinceMs;
    const left = STARTUP_THRESHOLDS_MS.failed - elapsed;
    if (left <= 0) return undefined;
    const timer = setTimeout(() => {
      setPlayback({
        kind: 'failed',
        attempt: playback.attempt,
        error: {
          code: 'stream.start_timeout',
          params: {},
          failedStep: 6,
          retryable: true,
          atMs: Date.now(),
        },
      });
    }, left);
    return () => clearTimeout(timer);
  }, [playback]);

  /*
    La ripetizione automatica, decisa dal dominio e non da qui.

    `retryDelayMs` dice se e quando: un decoder che rifiuta il flusso non si riprova mai, un avvio
    fallito una volta sola, una caduta in visione tre. Questa schermata non conosce nessuno di quei
    numeri, e non deve — sarebbero la stessa tabella scritta una seconda volta.
  */
  useEffect(() => {
    if (playback.kind !== 'failed' || !channel) return undefined;

    if (retryPolicyFor(playback.error.code).kind === 'back-to-login') {
      backToLoginRef.current(playback.error);
      return undefined;
    }

    /*
      Il tentativo **successivo**, non sempre il primo.

      `retryDelayMs` risponde `null` quando i tentativi sono finiti, ed è così che ci si ferma:
      fermarsi è un passo del procedimento, non la sua assenza. Con un numero fisso quella risposta
      non arrivava mai, e l'app riprovava all'infinito — mostrando a turno l'errore e l'attesa, che
      da fuori è l'app che «va in loop».
    */
    const next = playback.attempt + 1;
    const delay = retryDelayMs(playback.error.code, next);
    if (delay === null) {
      /*
        Tentativi finiti: si apre la guida.

        Lasciare una schermata d'errore ferma è un vicolo cieco — l'unica uscita sarebbe Indietro,
        che riporta all'accesso: per un canale che non parte si finirebbe a ridigitare le
        credenziali. Aprendo l'elenco, chi guarda ha subito la cosa che vuole fare, cioè **scegliere
        un altro canale**.

        L'errore non sparisce: resta scritto sopra la griglia. Un elenco che compare da solo senza
        dire perché si legge come un'app che ha cambiato idea.
      */
      /*
        **Arrendersi vuol dire anche smettere di chiedere.**

        Prima qui si apriva solo l'elenco, e il lettore restava vivo: continuava a scaricare per un
        canale che nessuno stava più guardando, mentre chi guardava sceglieva altro. Sommato ai
        tentativi senza fondo di hls.js, è quello che il 27-09-2026 ha fatto bloccare l'indirizzo dal
        gateway una ventina di volte in una sera — `delivery_rate_limit`, 3000 richieste ogni quarto
        d'ora.

        Un lettore che nessuno ascolta non deve restare acceso: distruggerlo chiude anche la sessione
        sulla linea, che è l'altra risorsa che si stava consumando per niente.
      */
      stopPlayer();
      setGuideOpen(true);
      return undefined;
    }

    retryTimer.current = setTimeout(() => startRef.current(channel, next), delay);
    return () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [playback, channelId, stopPlayer]);

  /*
    Il controllore della guida vive quanto la schermata, non quanto l'apertura.

    Ricostruirlo a ogni apertura butterebbe via la posizione — chi apre la guida, guarda, chiude e
    riapre si ritroverebbe in cima all'elenco ogni volta — e su dodicimila canali significa non
    ritrovare più dov'era.
  */
  const categories = useMemo(
    () => buildGuideCategories(channels, favoriteIds, categoryNames),
    [channels, favoriteIds, categoryNames],
  );

  /*
    I canali della categoria scelta. Con «Tutti» è lo stesso elenco, senza copiarlo.

    Da qui nasce una trappola che vale la pena nominare: il cursore della guida indicizza **questo**
    elenco, mentre `index` — il canale che si sta guardando — indicizza quello intero. Confonderli
    manda a guardare un canale diverso da quello premuto appena si filtra, e il difetto non si vede
    mai con «Tutti» attivo, cioè non si vede in sviluppo. Per questo il passaggio fra i due avviene
    sempre attraverso l'**identificativo**, mai attraverso il numero di posizione.
  */
  const guideChannels = useMemo(
    () => channelsOfCategory(channels, categoryId, favoriteIds),
    [channels, categoryId, favoriteIds],
  );

  /*
    Il magazzino ristretto alla categoria: senza, la griglia leggerebbe i programmi del canale
    sbagliato appena si filtra. `select` dell'insieme intero restituisce sé stesso, quindi il caso
    più comune non copia niente.
  */
  const guidePrograms = useMemo(() => {
    const position = new Map(channels.map((item, at) => [item.id, at]));
    const indices = guideChannels
      .map((item) => position.get(item.id))
      .filter((at): at is number => at !== undefined);
    return programs.select(indices);
  }, [programs, channels, guideChannels]);

  /**
   * Scegliere una cella della guida: la stessa cosa che fa OK, e che fa un clic.
   *
   * Sta in una funzione perché i due percorsi devono **essere** lo stesso percorso. Scritta due
   * volte, la versione del mouse sarebbe nata senza il controllo sui programmi assenti o senza il
   * passaggio per identificativo — cioè con i difetti che quella del telecomando ha già superato, e
   * che nessuna prova del telecomando può trovare.
   */
  /**
   * Scegliere una cella della guida: **si apre il canale**.
   *
   * ## Il difetto che questo sostituisce
   *
   * Prima questo percorso trattava ogni scelta come una richiesta di replica: premere su un
   * programma di due ore fa, o di stasera, su un canale senza archivio rispondeva «catch-up non
   * disponibile» e non apriva niente. L'errore era vero e fuori tema — chi preme su una riga della
   * guida sta dicendo «voglio questo canale», e il programma su cui ha premuto dice **quale**
   * momento gli interessa, non che rifiuti la diretta se quel momento non c'è.
   *
   * Quindi adesso: il canale si apre sempre. Se l'archivio copre l'inizio del programma scelto, la
   * fascia **propone** «Guarda dall'inizio» — una proposta, che si può ignorare, invece di un
   * rifiuto che toglie anche quel che si poteva avere.
   *
   * Sta in una funzione perché il tasto OK e il clic del mouse devono **essere** lo stesso percorso:
   * scritta due volte, la versione del mouse nascerebbe senza i controlli che quella del telecomando
   * ha già superato.
   */
  const chooseFromGuide = useCallback(
    (cursor: GuideCursor) => {
      /*
        Il canale si prende dall'elenco **della guida**, e la sua posizione in quello intero si
        ritrova per identificativo: con un filtro di categoria attivo le due posizioni divergono, e
        confonderle aprirebbe un canale diverso da quello premuto, in silenzio.
      */
      const target = guideChannels[cursor.channelIndex];
      const fullIndex = target ? channels.findIndex((item) => item.id === target.id) : -1;
      setGuideOpen(false);
      if (fullIndex >= 0) setIndex(fullIndex);
      if (!target) return;

      /*
        La proposta, quando c'è qualcosa da proporre.

        Senza EPG non c'è nessun programma sotto il cursore — su stage è il caso di tutti i canali —
        e allora si apre e basta: nessuna proposta, nessun errore.
      */
      const hasProgram =
        cursor.slot >= 0 && cursor.slot < guidePrograms.programCountOf(cursor.channelIndex);
      if (!hasProgram) {
        setPendingReplay(null);
        return;
      }

      const startMs = guidePrograms.startMsOf(cursor.channelIndex, cursor.slot);
      const program = guidePrograms.materialize(cursor.channelIndex, cursor.slot);
      /*
        `canWatchFromStart` e non `catchupVerdict`: la domanda è «l'archivio copre quell'istante»,
        non «questa è una replica riproducibile». Un programma **in onda** con l'archivio si può
        guardare dall'inizio — è il caso della partita cominciata venti minuti fa — e `catchupVerdict`
        lì risponde `still-live`, che è giusto per la domanda sbagliata.
      */
      setPendingReplay(
        canWatchFromStart(target, startMs, serverNowMs)
          ? { startMs, title: program.title }
          : null,
      );
    },
    [guideChannels, guidePrograms, channels, serverNowMs],
  );

  /**
   * L'anteprima: il canale parte **senza** chiudere la guida.
   *
   * Sostituisce quello che stava andando, e non se ne aggiunge uno secondo: due sessioni aperte
   * insieme consumano il limite di connessioni della linea, ed è il modo in cui uno zapping
   * esplorativo diventa «la linea dice che sto già guardando altrove».
   *
   * Non fa nient'altro che spostare l'indice: la partenza la decide l'effetto dello zapping, con i
   * suoi 400 ms d'attesa. Ed è voluto — chi scorre l'elenco premendo OK di seguito non deve aprire
   * una sessione a ogni pressione.
   */
  const previewFromGuide = useCallback(
    (cursor: GuideCursor) => {
      const target = guideChannels[cursor.channelIndex];
      if (!target) return;
      const fullIndex = channels.findIndex((item) => item.id === target.id);
      if (fullIndex >= 0) setIndex(fullIndex);
    },
    [guideChannels, channels],
  );

  /*
    I tasti della fascia, letti da un riferimento.

    L'iscrizione al telecomando non può averli fra le dipendenze — si rifarebbe a ogni disegno — e
    nemmeno copiarli, o premerebbe la versione di quando si è iscritta: «Aggiungi ai preferiti» dopo
    che il canale è già stato aggiunto.
  */
  const osdActionsRef = useRef<ReadonlyArray<{ id: string; label: string; onSelect: () => void }>>([]);

  // Lette da un riferimento dentro l'iscrizione al telecomando, per non rifarla a ogni disegno.
  const chooseFromGuideRef = useRef(chooseFromGuide);
  const previewFromGuideRef = useRef(previewFromGuide);
  useEffect(() => {
    chooseFromGuideRef.current = chooseFromGuide;
    previewFromGuideRef.current = previewFromGuide;
  }, [chooseFromGuide, previewFromGuide]);

  /*
    **Il rimedio all'audio, senza interfaccia.**

    Il browser rifiuta l'avvio automatico col suono finché l'utente non ha compiuto un gesto. Qualsiasi
    pressione di tasto **è** quel gesto: quindi alla prima si riaccende, e l'avviso sparisce senza che
    nessuno debba premere niente apposta.

    È il posto giusto per un rimedio del genere: invisibile, e dentro la cosa che lo rende possibile.
    Un tasto dedicato — quello che avevo messo — chiede all'utente di risolvere un problema che non è
    suo e che l'app può risolvere da sé.
  */
  useEffect(() => {
    if (!mutedByPolicy) return undefined;
    return focusPort.subscribe(() => {
      playerRef.current?.setMuted(false);
      setMutedByPolicy(false);
      // Non si consuma: il tasto premuto deve fare **anche** quello per cui è stato premuto.
      return false;
    });
  }, [mutedByPolicy, focusPort]);

  const guide = useMemo(
    () =>
      new GuideController({
        channels: guideChannels,
        programs: guidePrograms,
        window: guideWindow,
        // La larghezza si **calcola**: era `990` scritto a mano, e lo stesso numero stava in altri
        // tre posti. Cambiando i pixel al minuto, tre di quei quattro sarebbero rimasti indietro.
        viewport: { heightPx: 700, timelineWidthPx: windowWidthPx(guideWindow) },
      }),
    [guideChannels, guidePrograms, guideWindow],
  );
  useEffect(() => () => guide.dispose(), [guide]);

  /*
    I programmi si chiedono **solo** quando il dito si è fermato, e per i canali della **guida**.

    Non di `channels`: il cursore indicizza l'elenco filtrato, e leggere l'elenco intero con quel
    numero chiederebbe i programmi di canali che non sono sullo schermo — restando invece senza quelli
    che ci sono. Con «Tutti» attivo i due elenchi coincidono, quindi il difetto non si presenterebbe
    mai in sviluppo: si presenta al primo cliente che filtra per categoria.
  */
  useEffect(() => {
    return guide.onSettle((cursor) => {
      const primo = Math.max(0, cursor.channelIndex - 4);
      const ultimo = Math.min(guideChannels.length, cursor.channelIndex + 8);
      const visibili = guideChannels.slice(primo, ultimo).map((c) => c.id);
      if (visibili.length > 0) onNeedPrograms(visibili);
    });
  }, [guide, guideChannels, onNeedPrograms]);

  // Indietro, sempre e subito: prima di ogni altra cosa, senza aspettare il tempo massimo.
  useEffect(() => {
    const release = focusPort.subscribe((key) => {
      /*
        Quando la guida è aperta i tasti sono suoi, e un'unica iscrizione li smista.

        Due iscrizioni separate — una della diretta, una della guida — li riceverebbero **entrambe**:
        una freccia giù muoverebbe il cursore della guida *e* cambierebbe canale sotto. È il genere
        di difetto che si vede solo su un apparecchio vero, dove è pure difficile da raccontare.
      */
      if (menuHasFocus) return false; // i tasti sono del menu, che ha la sua iscrizione

      if (guideOpen) {
        if (key.kind === 'back') {
          // Indietro dalle categorie chiude la guida; dalla griglia torna prima alle categorie:
          // è il percorso inverso di come ci si è arrivati, che è quello che il tasto promette.
          if (guideFocus === 'grid' && categoryId !== CATEGORY_ALL) {
            setGuideFocus('categories');
            return true;
          }
          /*
            **Il ripensamento.**

            Chi è entrato nella guida per sbaglio, o ha cambiato idea dopo aver sbirciato due canali
            in anteprima, con Indietro torna a quello che stava guardando. Senza, l'unico modo
            sarebbe ricordarsi il canale e ricercarlo nell'elenco — su dodicimila canali, non si fa.

            Si riporta l'indice solo se è **cambiato**: altrimenti si riavvierebbe la sessione del
            canale che sta già andando, cioè si pagherebbe l'attesa d'avvio per non fare niente.
          */
          const before = indexBeforeGuide.current;
          if (before !== null && before !== index) setIndex(before);
          indexBeforeGuide.current = null;
          setGuideOpen(false);
          setGuideFocus('grid');
          return true;
        }

        if (guideFocus === 'categories') {
          if (key.kind === 'select') {
            const entry = categories[categoryCursor];
            if (entry) {
              setCategoryId(entry.id);
              // Scegliendo si passa alla griglia: restare sulla colonna obbliga a una freccia in più
              // per fare la cosa per cui si è scelto.
              setGuideFocus('grid');
            }
            return true;
          }
          if (key.kind !== 'direction') return false;
          if (key.direction === 'up' || key.direction === 'down') {
            setCategoryCursor((at) =>
              moveCategoryCursor(at, key.direction === 'up' ? -1 : 1, categories.length),
            );
            return true;
          }
          if (key.direction === 'right') {
            setGuideFocus('channel');
            return true;
          }
          // Sinistra dalla colonna esce verso il menu: è l'unica cosa che c'è a sinistra.
          requestMenuRef.current();
          return true;
        }

        if (guideFocus === 'channel') {
          if (key.kind === 'select') {
            // OK sulla card: aggiunge o toglie dai preferiti. È il gesto del disegno («OK sul logo»),
            // e adesso ha un bersaglio che il telecomando raggiunge.
            const target = guideChannels[guide.currentCursor.channelIndex];
            if (target) onToggleFavoriteRef.current(target.id);
            return true;
          }
          if (key.kind !== 'direction') return false;
          if (key.direction === 'right') {
            setGuideFocus('grid');
            return true;
          }
          if (key.direction === 'left') {
            setGuideFocus('categories');
            return true;
          }
          return guide.move(key.direction);
        }

        if (key.kind === 'select') {
          /*
            **Un programma che non è ancora cominciato non si guarda: si ricorda.**

            È quel che fa il disegno (`p.onClick`: live → player, altrimenti → promemoria), ed era
            l'unica azione della guida che non esisteva. Il promemoria si mette **prima** dei due
            passi dell'anteprima, perché su un programma futuro non c'è niente da vedere in anteprima:
            il canale sta trasmettendo altro.
          */
          const atCursor = guide.currentCursor;
          const onFuture = (() => {
            const target = guideChannels[atCursor.channelIndex];
            if (!target) return null;
            if (programs.programCountOf(atCursor.channelIndex) === 0) return null;
            const program = programs.materialize(atCursor.channelIndex, atCursor.slot);
            return program.startMs > serverNowMs ? { channelId: target.id, startMs: program.startMs } : null;
          })();
          if (onFuture !== null && onToggleReminderRef.current !== undefined) {
            onToggleReminderRef.current(onFuture.channelId, onFuture.startMs);
            return true;
          }

          /*
            **Due passi, non uno.**

            Il primo OK su un canale lo mette in **anteprima**: parte qui dentro, in piccolo, al posto
            di quello che stava andando. Il secondo OK sullo stesso canale lo apre a schermo intero.

            Serve perché scegliere un canale da un elenco è una scommessa: il nome non dice cosa c'è
            adesso, e su un catalogo senza EPG non lo dice proprio nessuno. Con un passo solo, l'unico
            modo di sapere se è quello giusto era aprirlo a schermo intero e poi tornare indietro — e
            ogni giro di quelli è una sessione aperta e chiusa sulla linea.

            «Lo stesso canale» si misura sull'**identificativo**, non sulla posizione del cursore: fra
            un'anteprima e l'altra si può cambiare categoria, e la stessa posizione sarebbe un canale
            diverso.
          */
          const cursor = guide.currentCursor;
          const target = guideChannels[cursor.channelIndex];
          const alreadyPreviewing = target !== undefined && target.id === channel?.id;

          if (!alreadyPreviewing) {
            previewFromGuideRef.current(cursor);
            return true;
          }
          chooseFromGuideRef.current(cursor);
          return true;
        }
        if (key.kind === 'direction') {
          /*
            Sinistra dal bordo sinistro della griglia va **sulla card del canale**, e solo da lì alle
            categorie. `move` risponde `false` quando non ha dove andare, ed è lì che si scavalca.

            Su e giù dalla card muovono la riga: restare fermi mentre il resto della griglia scorre
            darebbe un fuoco che non si capisce a cosa appartenga.
          */
          if (key.direction === 'left' && !guide.move('left')) {
            setGuideFocus('channel');
            return true;
          }
          if (key.direction === 'left') return true;
          return guide.move(key.direction);
        }
        return false;
      }

      if (key.kind === 'select') {
        /*
          **Il primo OK apre la fascia**, non la guida.

          La cosa che si fa più spesso mentre si guarda è mettere il canale fra i preferiti, e prima
          per arrivarci bisognava passare dalla guida — cioè lasciare il canale per tornare al canale.
          Ora OK mostra la fascia e le dà i tasti; alla guida ci si arriva dal suo primo tasto, che è
          un posto dove la si va a cercare.
        */
        if (playback.kind === 'playing') {
          if (osdCursor === null) {
            setOsdVisible(true);
            setOsdCursor(0);
            return true;
          }
          // Il secondo OK preme il tasto a fuoco.
          osdActionsRef.current[osdCursor]?.onSelect();
          return true;
        }

        /*
          Non si sta guardando: niente fascia, e OK apre la guida.

          La fascia esiste **solo mentre il video va** — è lì che ha senso «aggiungi ai preferiti».
          Durante l'attesa o dopo un errore darebbe il fuoco a una barra che non si vede: tasti
          premibili e invisibili, che è il difetto peggiore di tutti. E in quel momento la cosa che
          serve è proprio l'elenco dei canali.
        */
        const at = guideChannels.findIndex((item) => item.id === channel?.id);
        if (at >= 0) guide.focusChannel(at);
        indexBeforeGuide.current = index;
        setGuideOpen(true);
        return true;
      }

      if (key.kind === 'back') {
        /*
          Indietro fa **due cose in fila**, e l'ordine conta.

          Prima toglie la fascia in basso e lascia solo il video: è la richiesta esplicita, e ha una
          ragione — i tasti dei preferiti servono per un istante e poi stanno davanti all'immagine.
          Solo a schermo già pulito, un secondo Indietro chiede il menu.

          Con una pressione sola che facesse entrambe le cose, lo schermo pulito sarebbe
          irraggiungibile: è la differenza fra un tasto che ha due significati e un tasto che ne ha
          uno solo, sbagliato metà delle volte.

          Uscire di colpo non lo fa mai: riporterebbe all'accesso, cioè a ridigitare le credenziali,
          e per chi voleva solo cambiare canale è una punizione.
        */
        if (osdCursor !== null) {
          // Prima si restituiscono i tasti al lettore, lasciando la fascia visibile: chi l'ha aperta
          // per sbaglio non deve perderla di vista per ritrovarla.
          setOsdCursor(null);
          return true;
        }
        if (osdVisible && playback.kind === 'playing') {
          setOsdVisible(false);
          return true;
        }
        requestMenuRef.current();
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'up') {
        zap(-1);
        return true;
      }
      if (key.direction === 'down') {
        zap(1);
        return true;
      }
      /*
        Sinistra e destra scorrono i tasti della fascia, ma **solo quando la fascia li ha**.

        Senza quel «solo», si inghiottirebbero due tasti che a schermo pulito non sono nostri: la
        piattaforma ci fa cose sue, e un'app che li consuma senza usarli sembra bloccata.
      */
      if (osdCursor !== null) {
        const total = osdActionsRef.current.length;
        if (total === 0) return false;
        const delta = key.direction === 'right' ? 1 : -1;
        // Ai bordi si resta, come ovunque: avvolgere su tre tasti fa premere due volte per tornare
        // dove si era.
        setOsdCursor((at) => Math.min(Math.max((at ?? 0) + delta, 0), total - 1));
        return true;
      }
      return false;
    });
    return release;
  }, [
    focusPort,
    zap,
    guideOpen,
    guideFocus,
    guide,
    guideChannels,
    guidePrograms,
    channels,
    channel?.id,
    categories,
    categoryCursor,
    categoryId,
    menuHasFocus,
    serverNowMs,
    osdVisible,
    osdCursor,
    playback.kind,
    index,
  ]);

  useEffect(() => () => stopPlayer(), [stopPlayer]);

  /*
    Il programma in onda e il successivo, per l'OSD.

    Si leggono dal magazzino **intero**, indicizzato come `channels`: è il canale che si sta guardando,
    non quello a fuoco nella guida, e usare qui il magazzino filtrato darebbe il programma di un altro
    canale appena si sceglie una categoria.

    Quando l'EPG non copre il canale — su stage sono 60 canali su 60 senza mappatura — `slotAt`
    risponde `-1` e qui restano `null`: l'OSD scrive «programma non disponibile» e **non** disegna la
    barra. Una barra a zero direbbe «è appena cominciato», che è una bugia plausibile.
  */
  const nowProgram: Program | null = useMemo(() => {
    if (!channel) return null;
    const at = programs.channelIds.indexOf(channel.id);
    if (at < 0) return null;
    const slot = programs.slotAt(at, serverNowMs);
    return slot < 0 ? null : programs.materialize(at, slot);
  }, [programs, channel, serverNowMs]);

  /*
    «Poi» è il primo programma che **comincia dopo adesso**.

    Era «lo slot successivo a quello in onda», e con un elenco disordinato o con un doppione dava il
    programma **in corso**: sullo schermo si leggeva «The Bayou» come titolo e «Poi: The Bayou»
    accanto. L'ordinamento adesso è garantito dal magazzino, ma la definizione per istante è giusta
    comunque — e non dipende da un presupposto che qualcun altro deve mantenere.

    Si salta anche ciò che è già cominciato: un programma che si sovrappone a quello in onda non è
    «poi», qualunque cosa dica il suo indice.
  */
  const nextProgram: Program | null = useMemo(() => {
    if (!channel) return null;
    const at = programs.channelIds.indexOf(channel.id);
    if (at < 0) return null;
    const count = programs.programCountOf(at);
    for (let slot = Math.max(programs.slotAt(at, serverNowMs), 0); slot < count; slot += 1) {
      if (programs.startMsOf(at, slot) > serverNowMs) return programs.materialize(at, slot);
    }
    return null;
  }, [programs, channel, serverNowMs]);

  /*
    I tasti dell'OSD.

    Sono quelli che **funzionano**: guida e preferiti. Il prototipo ne mostra quattro, e i due che
    mancano — sottotitoli e tracce audio — non hanno niente dietro. Una voce che c'è e non fa nulla è
    peggio di una che manca: chi la preme e non ottiene niente smette di fidarsi della barra intera.
  */
  const osdActions: ReadonlyArray<{ id: string; label: string; onSelect: () => void }> = useMemo(
    () => [
      {
        id: 'guide',
        label: translator.t('player.action.guide'),
        onSelect: () => {
          /*
            **Col dito la guida è una sezione, non un sovrapposto.**

            Col telecomando questo tasto apre la griglia **sopra** il canale, che continua ad andare
            in un riquadro: è il senso della guida sul televisore — si sfoglia senza perdere quel che
            si sta guardando. Sul telefono quella griglia non esiste: la guida è una schermata sua,
            con l'elenco che si apre, e il canale a schermo pieno sotto non ci starebbe comunque.

            Premendolo non succedeva **niente**. La griglia veniva aperta, ma su 390 px non si vede
            né si percorre — un tasto che risponde al tocco e non porta da nessuna parte, che è il
            difetto peggiore di una barra di comandi: chi lo prova una volta smette di fidarsi anche
            degli altri.

            Si chiede a chi ospita di uscire dalla riproduzione, che è lo stesso gesto di Indietro e
            porta esattamente dove il tasto promette.
          */
          if (touch) {
            requestMenuRef.current();
            return;
          }
          const at = guideChannels.findIndex((item) => item.id === channel?.id);
          if (at >= 0) guide.focusChannel(at);
          setGuideOpen(true);
        },
      },
      ...(pendingReplay === null
        ? []
        : [
            {
              id: 'restart',
              // Il **titolo** nel tasto, non «guarda dall'inizio» e basta: chi ha premuto su una
              // riga della guida deve riconoscere che la proposta riguarda quello che ha scelto.
              label: translator.t('player.action.watchFromStart', { title: pendingReplay.title }),
              onSelect: () => {
                if (!channel) return;
                startRef.current(channel, 0, catchupUrlForRef.current(channel, pendingReplay.startMs));
                // La proposta si consuma: ripresentarla mentre si sta già guardando dall'inizio
                // sarebbe un tasto che rifà quel che ha appena fatto.
                setPendingReplay(null);
              },
            },
          ]),
      {
        id: 'favorite',
        /*
          **Una stella, non una frase.**

          C'era scritto «Aggiungi ai preferiti» / «Togli dai preferiti»: due etichette lunghe che
          occupano metà barra per dire quel che un simbolo dice da solo. Piena quando è preferito,
          vuota quando non lo è — e premerla inverte, che è l'unica cosa che una stella può fare.
        */
        label: favoriteIds.has(channel?.id ?? '') ? '★' : '☆',
        onSelect: () => {
          if (channel) onToggleFavoriteRef.current(channel.id);
        },
      },
    ],
    [translator, guide, guideChannels, channel, favoriteIds, pendingReplay, touch],
  );

  useEffect(() => {
    osdActionsRef.current = osdActions;
  }, [osdActions]);

  if (!channel) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Busy label={translator.t('live.noChannels')} />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      {/*
        **Un solo elemento video, che cambia posto.**

        Con la guida aperta si rimpicciolisce in alto a destra invece di sparire: il canale continua
        ad andare, si vede cosa si sta lasciando, e l'anteprima del canale che si sta scegliendo è
        lì. Smontarlo e rimontarlo chiuderebbe la sessione e ne aprirebbe un'altra a ogni apertura
        della guida — cioè il costo peggiore per l'effetto peggiore.

        Sta **sopra** l'elenco (`zIndex`) ma in un angolo, così non copre né le categorie a sinistra
        né le righe: la selezione resta leggibile, che era la condizione posta.
      */}
      <video
        ref={videoRef}
        playsInline
        /*
          **`muted` non si dichiara qui**, ed è un difetto che è costato l'audio.

          React tratta `muted` come una proprietà del DOM e la riscrive a ogni disegno in cui il
          valore differisce. L'adattatore mette `video.muted = true` quando il browser rifiuta
          l'avvio automatico con l'audio; poi bastava un qualunque ridisegno — e mentre il canale
          parte ce n'è uno al secondo — perché React rimettesse `false`. Due padroni per lo stesso
          dato, che se lo riscrivono a vicenda: il risultato è un video che il browser mette in
          pausa per violazione della politica, oppure resta in muto e il tasto «Attiva audio» non
          cambia niente perché il disegno successivo lo annulla.

          Il padrone è l'adattatore, che è l'unico a sapere se la piattaforma ha rifiutato. Qui
          l'elemento si lascia **non controllato** su questo attributo.
        */
        data-video={guideOpen ? 'preview' : 'full'}
        style={
          guideOpen
            ? { ...PREVIEW_BOX, objectFit: 'cover' }
            : { position: 'absolute', inset: 0, width: '100%', height: '100%', background: colors.playerBackground }
        }
      />

      {/*
        **Il tocco che richiama i comandi.**

        Sta sopra il video e sotto tutto il resto, e vale solo col dito: col telecomando i tasti
        parlano già, e un bersaglio invisibile che intercetta OK ruberebbe la pressione a chi apre la
        fascia. Qui invece è l'unico modo di rifarla comparire dopo che si è ritirata — senza, si
        ritirerebbe per sempre, che è peggio del difetto che stiamo correggendo.

        Si **alterna**: un tocco la mostra, un altro la toglie. È quel che fa qualunque lettore, e
        toglierla è la ragione per cui si tocca lo schermo la seconda volta.
      */}
      {touch && !guideOpen && (
        <button
          type="button"
          data-touch-surface="1"
          aria-label={translator.t(osdVisible ? 'live.osd.hide' : 'live.osd.show')}
          onClick={() => {
            setOsdVisible((prima) => !prima);
            // Il fuoco della fascia resta roba del telecomando: col dito si preme quel che si vede.
            setOsdCursor(null);
          }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            border: 0,
            padding: 0,
            background: 'transparent',
            cursor: 'pointer',
          }}
        />
      )}

      {/*
        **Il tasto dello schermo intero**, solo col dito e solo mentre si guarda.

        In verticale l'immagine divide lo spazio con il resto dell'interfaccia, che su 390 px è
        disegnata per un televisore ed è troppo grande: il tasto è la via d'uscita. In orizzontale ci
        si va da sé — girare il telefono *è* la richiesta — e il tasto serve a tornare indietro.

        Sta dentro la fascia e non fisso in un angolo: a schermo intero, con la fascia ritirata, non
        deve restare niente davanti all'immagine.
      */}
      {touch && onToggleFullscreen !== undefined && osdVisible && !guideOpen && (
        <button
          type="button"
          data-fullscreen-toggle="1"
          onClick={onToggleFullscreen}
          aria-label={translator.t(fullscreen ? 'live.fullscreen.exit' : 'live.fullscreen.enter')}
          style={{
            position: 'absolute',
            zIndex: 7,
            right: 16,
            top: `calc(16px + env(safe-area-inset-top, 0px))`,
            width: 44,
            height: 44,
            borderRadius: 22,
            border: `1px solid ${colors.border}`,
            background: colors.background,
            color: colors.text,
            font: `700 18px ${FONT_STACK}`,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          {fullscreen ? '⤡' : '⤢'}
        </button>
      )}

      {/*
        **L'attesa dentro il riquadro dell'anteprima.**

        Senza, il riquadro restava nero mentre il canale partiva: identico a un canale che non
        arriverà mai, e per giunta proprio nel momento in cui si sta decidendo se sceglierlo. Un
        rettangolo nero non dice «sto caricando», dice «non c'è niente».

        Le attese a schermo intero qui sono soppresse — coprirebbero l'elenco — quindi questa è la
        sola che parla durante l'anteprima, e sta **esattamente** sopra il video: la geometria è una
        costante sola (`PREVIEW_BOX`), perché due elenchi di coordinate uguali si scostano al primo
        ritocco e nessuno se ne accorge finché non si vede il bordo nero che spunta.
      */}
      {guideOpen && playback.kind !== 'playing' && (
        <div
          data-preview-status="1"
          style={{
            ...PREVIEW_BOX,
            zIndex: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            boxSizing: 'border-box',
            textAlign: 'center',
          }}
        >
          {playback.kind === 'failed' ? (
            /*
              Anche il fallimento si dice **qui dentro**, in breve.

              Il riquadro nero dopo un tentativo fallito è la stessa bugia dell'attesa muta: chi
              guarda non sa se stia ancora provando. Il codice e l'ora restano alla schermata intera,
              che è dove li cerca chi risponde al telefono — qui ci sta una riga sola.
            */
            <span data-preview-error="1" style={{ font: `600 20px ${FONT_STACK}`, color: colors.errorText }}>
              {translator.t(`errors.${playback.error.code}`, playback.error.params)}
            </span>
          ) : (
            <Busy
              title={channel.name}
              label={
                playback.kind === 'starting'
                  ? phaseLabel(translator, startupPhase(Date.now() - playback.sinceMs))
                  : translator.t('live.tuning')
              }
            />
          )}
        </div>
      )}

      {/*
        L'OSD del prototipo, **mentre si guarda**.

        Con la guida aperta no: lì l'elenco dice già dove si è, e il titolo grande trapasserebbe sotto
        le righe. Con un'attesa o un errore nemmeno — quelle schermate hanno il loro testo al centro, e
        due informazioni sovrapposte sullo stesso schermo si contendono lo sguardo.
      */}
      {!guideOpen && playback.kind === 'playing' && osdVisible && (
        <PlayerOverlay
          touch={touch}
          /*
            Il numero **del canale**, e basta.

            Qui c'era «Canale 5 di 1200», e diceva due cose sbagliate insieme. Il totale non serve a
            nessuno mentre si guarda — è l'ampiezza del listino, non un'informazione sul canale — e
            quel «5» non era nemmeno il numero del canale: era la sua **posizione nell'elenco**, che
            cambia al variare del filtro e non corrisponde a niente di stampato sul telecomando.

            Chi guarda vuole leggere lo stesso numero che digita per arrivarci.
          */
          channelLine={translator.t('live.position', { number: channel.number })}
          programTitle={nowProgram?.title ?? null}
          startLabel={nowProgram ? translator.time(nowProgram.startMs) : undefined}
          endLabel={nowProgram ? translator.time(nowProgram.endMs) : undefined}
          percent={nowProgram ? programProgressPercent(nowProgram, serverNowMs) : null}
          nextLabel={nextProgram ? translator.t('player.next', { title: nextProgram.title }) : undefined}
          notice={mutedByPolicy ? translator.t('player.mutedByPolicy') : undefined}
          focusedAction={osdCursor === null ? null : (osdActions[osdCursor]?.id ?? null)}
          actions={osdActions}
        />
      )}

      {!guideOpen && playback.kind === 'starting' && (
        <Busy
          fullscreen
          title={channel.name}
          label={phaseLabel(translator, startupPhase(Date.now() - playback.sinceMs))}
          hint={estimateHint(translator, startupEstimateMs)}
          back={translator.t('live.backAlways')}
        />
      )}

      {!guideOpen && playback.kind === 'idle' && (
        // L'attesa breve fra una pressione e la partenza: 400 ms, ma se il dito resta premuto dura
        // quanto lo zapping. Senza niente qui, lo schermo sembrerebbe congelato.
        <Busy fullscreen title={channel.name} label={translator.t('live.tuning')} />
      )}


      {guideOpen && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            /*
              Il fondo dell'app, **opaco**.

              Prima era un velo nero all'88% sopra il video: sembrava una scelta gentile e costa caro —
              il video continua a decodificare dietro un pannello che lo copre, cioè si paga il
              fotogramma più caro del sistema per non vederlo. E il prototipo qui ha una schermata, non
              una sovrapposizione.
            */
            background: colors.background,
            display: 'flex',
          }}
        >
          <GuideScreen
            controller={guide}
            programs={guidePrograms}
            window={guideWindow}
            serverNowMs={serverNowMs}
            categories={categories}
            categoryId={categoryId}
            categoryCursor={categoryCursor}
            onChooseCategory={(id, at) => {
              setCategoryCursor(at);
              setCategoryId(id);
              setGuideFocus('grid');
            }}
            favoriteIds={favoriteIds}
            reminderIds={reminderIds}
            focusArea={guideFocus}
            onOpenFavorites={onOpenFavorites}
            // Lo spazio dell'anteprima, più il respiro: la trama del programma non deve finirci sotto.
            previewReservePx={PREVIEW_WIDTH_PX + 48}
            backHint={
              /*
                «Indietro: torna a Rai 1» — e solo quando c'è davvero un canale a cui tornare.

                Si scrive il **nome**, non «al canale precedente»: chi è entrato nella guida per
                sbaglio deve riconoscere quello che stava guardando, non fidarsi di una promessa
                generica.
              */
              indexBeforeGuide.current !== null && channels[indexBeforeGuide.current]
                ? translator.t('guide.backTo', { channel: channels[indexBeforeGuide.current]?.name ?? '' })
                : undefined
            }
            /*
              Il clic: un colpo su una cella di programma **sceglie**, uno sulla colonna del canale
              si limita a spostare il cursore.

              I due gesti non sono simmetrici perché non dicono la stessa cosa. Su una cella si sa
              cosa si è premuto — quel programma, su quel canale. Sulla colonna si è detto soltanto
              «questa riga»: farne partire il canale trasformerebbe uno scorrimento col mouse in una
              raffica di sessioni aperte, cioè la stessa lezione dei 400 ms dello zapping.
            */
            onToggleFavorite={(channelId) => onToggleFavoriteRef.current(channelId)}
            onPick={(channelIndex, slot, confirmed) => {
              setGuideFocus('grid');
              guide.focusAt(channelIndex, slot);
              if (confirmed) chooseFromGuide({ channelIndex, slot });
            }}
            notice={
              /* Il motivo per cui l'elenco si è aperto, sopra l'elenco. */
              playback.kind === 'failed' ? (
                <div
                  data-guide-error="1"
                  style={{
                    flex: 'none',
                    marginBottom: 16,
                    padding: '14px 28px',
                    borderRadius: 14,
                    background: colors.errorPanel,
                    border: `1px solid ${colors.live}`,
                    font: `600 20px ${FONT_STACK}`,
                  }}
                >
                  {translator.t(`errors.${playback.error.code}`, playback.error.params)}{' '}
                  <span style={{ font: `400 15px ${FONT_STACK}`, color: colors.textMuted }}>
                    {playback.error.code} · {pipeline} · {new Date(playback.error.atMs).toISOString()}
                  </span>
                </div>
              ) : undefined
            }
          />
        </div>
      )}

      {!guideOpen && playback.kind === 'failed' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: colors.background,
            padding: 48,
            // Centrato come l'attesa: le due schermate si alternano nello stesso punto, e vederle
            // saltare da un angolo al centro fa sembrare che sia cambiata pagina.
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <div style={{ font: `700 30px ${FONT_STACK}`, marginBottom: 12 }}>{channel.name}</div>
          <div style={{ font: `500 22px ${FONT_STACK}`, marginBottom: 18 }}>
            {translator.t(`errors.${playback.error.code}`, playback.error.params)}
          </div>
          {/* Il codice e l'ora, in piccolo: non per chi guarda, per chi risponde al telefono. */}
          <div style={{ font: `400 14px ${FONT_STACK}`, color: colors.textMuted }}>
            {playback.error.code} · {pipeline} · {new Date(playback.error.atMs).toISOString()}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * L'anteprima dentro la guida: 480 × 270, cioè un sedicesimo di schermo a 16:9.
 *
 * Abbastanza grande da riconoscere cosa c'è, abbastanza piccola da non coprire né le categorie né le
 * righe — che è la condizione posta: non deve molestare la selezione del canale nuovo.
 */
/** Una mappa vuota **stabile**: costruirla in linea la farebbe cambiare a ogni disegno, e con essa
 *  le categorie di dodicimila canali. */
const EMPTY_CATEGORY_NAMES: ReadonlyMap<string, string> = new Map();

/**
 * Dopo quanto la fascia dei comandi si ritira, **col dito**.
 *
 * Tre secondi: il tempo di leggere che canale è e che programma sta andando. Col telecomando non si
 * ritira affatto — lì c'è Indietro, ed è un gesto esplicito che non va indovinato.
 */
const OSD_AUTOHIDE_MS = 3_000;

const PREVIEW_WIDTH_PX = 480;
const PREVIEW_HEIGHT_PX = 270;

/**
 * Il riquadro dell'anteprima: **una geometria sola**, per il video e per quel che ci va sopra.
 *
 * Erano due elenchi di coordinate identici, uno nello stile del video e uno nell'attesa. Due elenchi
 * uguali si scostano al primo ritocco — e il modo in cui si scoprono è un bordo nero che spunta da
 * sotto l'attesa, cioè guardando, mesi dopo.
 */
const PREVIEW_BOX = {
  position: 'absolute' as const,
  top: 52,
  right: 48,
  width: PREVIEW_WIDTH_PX,
  height: PREVIEW_HEIGHT_PX,
  borderRadius: 16,
  border: `2px solid ${colors.border}`,
  background: colors.playerBackground,
  overflow: 'hidden' as const,
  zIndex: 5,
};

/** I quattro testi dell'avvio. La fase la decide il dominio; qui si traduce. */
function phaseLabel(translator: Translator, phase: StartupPhase): string {
  return translator.t(`live.startup.${phase}`);
}

/**
 * La stima, solo se il server l'ha mandata.
 *
 * `startupEstimateSeconds` restituisce `null` quando non c'è, e qui quel `null` diventa **nessuna
 * riga** invece di una riga con un numero inventato.
 */
function estimateHint(
  translator: Translator,
  estimateMs: number | null | undefined,
): string | undefined {
  const seconds = startupEstimateSeconds(estimateMs);
  return seconds === null ? undefined : translator.t('live.startup.estimate', { seconds });
}
